/**
 * resolvePanelOutcomes.js
 *
 * Pure step-sequencing helper for ResolvePanel outcome handlers.
 *
 * Each outcome handler (handleSentResolved, handleFollowUp, handleClose,
 * handleMessageSent) builds an ordered array of step objects and delegates
 * sequencing here. The critical invariant: thread-close is always the LAST
 * step, so a mid-chain failure (e.g. checklist complete 500) leaves the
 * conversation OPEN and the rep can retry.
 *
 * Step shape:
 *   { name: string, run: () => Promise<any> }
 *
 * A step is considered failed when:
 *   - its `run()` throws, OR
 *   - its `run()` returns an object with `success === false`
 *
 * Return shape:
 *   success: true  → { success: true, results: [...] }
 *   success: false → { success: false, failedStep: string,
 *                      successfulSteps: string[], error: Error|undefined }
 */

// ---------------------------------------------------------------------------
// Lead-state guard helpers (spec #8 — archived / opted-out action gating)
// ---------------------------------------------------------------------------

/**
 * Returns true when the lead is in an archived state.
 *
 * A lead is considered archived when any of these fields is truthy:
 *   - is_archived: true
 *   - archived_at: set (non-null / non-empty)
 *   - is_spam: true  (spam leads go through the archive flow)
 *
 * @param {object|null|undefined} lead
 * @returns {boolean}
 */
export function isLeadArchived(lead) {
    return !!(lead?.is_archived || lead?.archived_at || lead?.is_spam);
}

/**
 * Returns true when the lead has opted out of communications
 * (and is NOT archived — archived takes precedence).
 *
 * is_open_for_communication === false is the only signal.
 * An archived lead may also have this field false; in that case
 * `isLeadArchived` wins — this function returns false so callers
 * that check only isLeadOptedOut are never misled into treating an
 * archived lead as merely opted-out.
 *
 * @param {object|null|undefined} lead
 * @returns {boolean}
 */
export function isLeadOptedOut(lead) {
    return !isLeadArchived(lead) && lead?.is_open_for_communication === false;
}

// Contact/outreach outcomes that are blocked for opted-out leads.
// OTHER ("Close / not interested") is deliberately excluded — it is an
// administrative action (archive/flag/plain-close) and must remain available.
// Exported so upcoming tasks (e.g. Archive/Spam confirm, T5) can enumerate
// contact-type outcomes from a single authoritative source.
export const CONTACT_OUTCOMES = new Set(['MESSAGE_SENT', 'SENT_RESOLVED', 'APPOINTMENT_BOOKED', 'FOLLOW_UP']);

// ---------------------------------------------------------------------------
// Close-reason lists (single source of truth — imported by ResolvePanel.jsx)
// ---------------------------------------------------------------------------

/** Reasons that archive the lead (move to Archived tab). */
export const ARCHIVE_REASONS = ['Not Interested', 'Too Expensive', 'Gone Elsewhere', 'Duplicate'];

/** Reasons that flag the lead as spam (also lands in Archived tab). */
export const FLAG_REASONS = ['Spam'];

// ---------------------------------------------------------------------------
// Confirm-gate predicate (spec #4 — Archive / Spam require a confirm step)
// ---------------------------------------------------------------------------

// Derived from the canonical lists above — cannot drift from them.
const CONFIRM_REASONS = new Set([...ARCHIVE_REASONS, ...FLAG_REASONS]);

/**
 * Returns true when the given close reason requires a confirmation dialog
 * before proceeding — i.e. it is an Archive or Spam reason that changes
 * lead state. Returns false for normal close reasons (Resolved, General
 * Chat) and for falsy values (no reason selected).
 *
 * @param {string|null|undefined} closeReason
 * @returns {boolean}
 */
export function closeRequiresConfirm(closeReason) {
    return Boolean(closeReason && CONFIRM_REASONS.has(closeReason));
}

/**
 * Returns true when the given outcome is allowed for the current lead state.
 *
 * Rules:
 *   archived → nothing allowed (block all outcomes)
 *   opted-out → CONTACT_OUTCOMES blocked, OTHER allowed
 *   normal → everything allowed
 *
 * @param {string} outcomeKey
 * @param {{ isArchived: boolean, isOptedOut: boolean }} state
 * @returns {boolean}
 */
export function isOutcomeAllowed(outcomeKey, { isArchived, isOptedOut }) {
    if (isArchived) return false;
    if (isOptedOut && CONTACT_OUTCOMES.has(outcomeKey)) return false;
    return true;
}

/**
 * Predicate: returns true when a step result represents a failure.
 *
 * Three shapes must be detected:
 *   1. { success: false }    — legacy contract from completeWorkflowItem
 *   2. { ok: false }         — callApi non-2xx / blocked / 401-retry failure
 *   3. thrown error          — caught by the .catch branch in runOutcomeSteps
 *
 * Note: a raw axios success with status 201 does NOT have ok===false, so
 * create responses are correctly treated as success.
 *
 * @param {any} result
 * @returns {boolean}
 */
function isStepFailure(result) {
    if (!result || typeof result !== 'object') return false;
    if (result.success === false) return true;
    if (result.ok === false) return true;
    return false;
}

/**
 * Run an ordered list of steps serially, stopping immediately on failure.
 * Implemented via promise-chained reduce to satisfy the no-await-in-loop /
 * no-restricted-syntax (for..of) lint rules.
 *
 * A step is considered failed when:
 *   - its `run()` throws, OR
 *   - its `run()` resolves to a value matching isStepFailure()
 *     (i.e. `{ success: false }` or `{ ok: false }`)
 *
 * @param {Array<{ name: string, run: () => Promise<any> }>} steps
 * @returns {Promise<{ success: boolean, failedStep?: string, successfulSteps?: string[], error?: Error, results?: any[] }>}
 */
export function runOutcomeSteps(steps) {
    // Accumulator carries the mutable state across the promise chain.
    const acc = { successfulSteps: [], results: [] };

    return steps.reduce(
        (chain, step) => chain.then((state) => {
            // A prior step already failed — propagate immediately.
            if (!state.running) return state;

            return Promise.resolve()
                .then(() => step.run())
                .then((result) => {
                    if (isStepFailure(result)) {
                        return {
                            running: false,
                            success: false,
                            failedStep: step.name,
                            successfulSteps: state.successfulSteps,
                            error: undefined,
                        };
                    }
                    state.successfulSteps.push(step.name);
                    state.results.push(result);
                    return state;
                })
                .catch((err) => ({
                    running: false,
                    success: false,
                    failedStep: step.name,
                    successfulSteps: state.successfulSteps,
                    error: err,
                }));
        }),
        Promise.resolve({ running: true, success: true, ...acc }),
    ).then((state) => {
        if (!state.success) {
            const { running: _r, ...rest } = state; // eslint-disable-line no-unused-vars
            return rest;
        }
        return { success: true, results: state.results };
    });
}

// ---------------------------------------------------------------------------
// Empty-state helper (spec #3 — no linked lead / no open task)
// ---------------------------------------------------------------------------

/**
 * Returns true when the Redux `whatsAppChatMetaData.leadDetails` slice has not
 * yet been populated for the current thread — i.e. the metadata fetch is still
 * in flight.  Once the fetch completes, Redux always sets leadDetails to either
 * a real object (id present) or an empty object {} (no linked lead); it is
 * never left undefined after loading.  Only `undefined` therefore signals
 * "not yet loaded" vs `{}` which means "loaded, no lead".
 *
 * @param {object|undefined|null} leadDetails - raw value from the Redux slice
 * @returns {boolean}
 */
export function isLeadHydrating(leadDetails) {
    return leadDetails === undefined;
}

/**
 * Returns a descriptor for the panel's empty state, or null when the panel
 * should render normally (lead + task both present, or still loading).
 *
 * Priority order:
 *   1. leadHydrating    → null       (metadata fetch in flight — avoid premature no-lead flash)
 *   2. !leadId          → 'no-lead'  (fetch complete, conversation not linked to any lead)
 *   3. workflowLoading  → null       (task fetch in flight — avoid flash)
 *   4. !workflowItem    → 'no-task'  (loaded, but no open task for this lead)
 *   5. otherwise        → null       (normal: lead + workflowItem present)
 *
 * The 'no-lead' and 'no-task' states both keep the outcome picker (incl. OTHER /
 * 'Close / not interested') visible so the rep can still resolve the conversation.
 *
 * @param {{ leadId: any, leadHydrating?: boolean, workflowLoading: boolean, workflowItem: any }} params
 * @returns {{ kind: 'no-lead'|'no-task', title: string, subtitle: string }|null}
 */
export function getEmptyStateMessage({ leadId, leadHydrating = false, workflowLoading, workflowItem }) {
    // While the thread's metadata (leadDetails) is still arriving from Redux,
    // treat this as loading — do NOT flash "No linked lead".
    if (leadHydrating) return null;
    if (!leadId) {
        return {
            kind: 'no-lead',
            title: 'No linked lead',
            subtitle: 'This conversation isn\'t linked to a lead.',
        };
    }
    if (workflowLoading) return null;
    if (!workflowItem) {
        return {
            kind: 'no-task',
            title: 'No open task for this lead',
            subtitle: 'You can still resolve the conversation.',
        };
    }
    return null;
}

/**
 * Build the step list for the MESSAGE_SENT (Sent — awaiting reply) outcome.
 *
 * Spec: note → checklist complete. No reminder-complete, no thread-close.
 * The conversation stays open — the rep is still mid-exchange — but the
 * completion card is shown so the rep can see the next workflow step.
 *
 * @param {{ leadId, practiceId, notes, treatmentId, callApi, completeWorkflowItem }} ctx
 * @returns {Array<{ name: string, run: () => Promise<any> }>}
 */
export function buildMessageSentSteps({ leadId, practiceId, notes, treatmentId, callApi, completeWorkflowItem }) {
    return [
        ...(leadId && practiceId ? [{
            name: 'note',
            run: () => callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/notes/`, 'post', {
                note: 'OTHER',
                additional_notes: notes || 'WhatsApp message sent from conversation',
                contacted_via: 'WHATSAPP',
                lead: leadId,
                ...(treatmentId ? { treatment: treatmentId } : {}),
            }),
        }] : []),
        // Keep-open: complete the workflow item and show the completion card;
        // there is deliberately no close step, so the thread stays open.
        {
            name: 'complete',
            run: () => completeWorkflowItem('MESSAGE_SENT', 'Message sent'),
        },
    ];
}

/**
 * Build the step list for the SENT_RESOLVED (Sent — resolved) outcome.
 *
 * Spec: note → checklist complete → thread close LAST. No reminder-complete.
 *
 * @param {{ leadId, practiceId, threadId, notes, treatmentId, callApi, completeWorkflowItem, dispatch, closeWhatsappConversationWithReason }} ctx
 * @returns {Array<{ name: string, run: () => Promise<any> }>}
 */
export function buildSentResolvedSteps({ leadId, practiceId, threadId, notes, treatmentId, callApi, completeWorkflowItem, dispatch, closeWhatsappConversationWithReason }) {
    return [
        ...(leadId && practiceId ? [{
            name: 'note',
            run: () => callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/notes/`, 'post', {
                note: 'OTHER',
                additional_notes: notes || 'WhatsApp message sent — conversation resolved',
                contacted_via: 'WHATSAPP',
                lead: leadId,
                ...(treatmentId ? { treatment: treatmentId } : {}),
            }),
        }] : []),
        // 'OTHER' + 'Resolved' maps to resolved_action: 'conversation_resolved' via getResolvedAction.
        {
            name: 'complete',
            run: () => completeWorkflowItem('OTHER', 'Resolved'),
        },
        // Thread-close is LAST: only runs if all prior steps (incl. checklist) succeeded.
        ...(threadId && leadId ? [{
            name: 'close',
            run: () => dispatch(closeWhatsappConversationWithReason(threadId, 'Resolved', leadId)),
        }] : []),
    ];
}

/**
 * Build the step list for the APPOINTMENT_BOOKED (Booked) outcome.
 *
 * Spec: complete the checklist item THEN close the thread as "Booked".
 * The thread-close is LAST so a checklist failure leaves the conversation open
 * and the rep can retry. No note step — the appointment record is the audit trail.
 *
 * @param {{ leadId, practiceId, threadId, completeWorkflowItem, dispatch, closeWhatsappConversationWithReason }} ctx
 * @returns {Array<{ name: string, run: () => Promise<any> }>}
 */
export function buildBookedSteps({ leadId, practiceId, threadId, completeWorkflowItem, dispatch, closeWhatsappConversationWithReason }) {
    return [
        // Checklist complete FIRST — the appointment was already created in BookingPanel;
        // this marks the linked workflow item done so the task advances correctly.
        ...(leadId && practiceId ? [{
            name: 'complete',
            run: () => completeWorkflowItem('APPOINTMENT_BOOKED', 'Booked'),
        }] : []),
        // Thread-close is LAST: only runs if checklist complete succeeded.
        ...(threadId && leadId ? [{
            name: 'close',
            run: () => dispatch(closeWhatsappConversationWithReason(threadId, 'Booked', leadId)),
        }] : []),
    ];
}

// ---------------------------------------------------------------------------
// Post-close list refresh helper (spec #7 — refresh conversation list after resolve)
// ---------------------------------------------------------------------------

/**
 * Dispatch a full conversation-list refresh after a successful close outcome.
 *
 * Called by each closing handler in ResolvePanel (handleSentResolved,
 * handleFollowUp, handleBooked, runClose) AFTER runOutcomeSteps returns.
 * Encapsulated here so the condition is testable in isolation without
 * mounting the heavy component.
 *
 * Conditions for dispatch:
 *   1. outcome.success must be true (partial failure → close never ran → skip).
 *   2. The step list must include a 'close' step (message-sent path has no close
 *      and the conversation stays open → no refresh needed).
 *
 * @param {{ outcome: object, steps: Array, dispatch: Function, getWhatsaAppThreads: Function }} params
 */
export function refreshAfterClose({ outcome, steps, dispatch, getWhatsaAppThreads }) {
    if (!outcome.success) return;
    const hasCloseStep = steps.some((s) => s.name === 'close');
    if (!hasCloseStep) return;
    dispatch(getWhatsaAppThreads());
}

// ---------------------------------------------------------------------------
// Treatment label helper (spec #5 — subtle treatment label on current task)
// ---------------------------------------------------------------------------

/**
 * Resolve the display name of the treatment linked to a workflow task.
 *
 * Resolution order:
 *   1. task.lead_treatment is a nested object → read treatment_name || treatment directly.
 *   2. task.lead_treatment is a numeric/string id → look it up in
 *      leadDetails.lead_treatments[] and return treatment_name || treatment.
 *   3. No match / missing data → return '' so the caller shows no trailing separator.
 *
 * This mirrors the field derivation in TaskBodyView.jsx:463
 * (`task?.lead_treatment?.treatment_name`) and TaskBoardView.jsx:653
 * (`sorted[0]?.treatment_name || sorted[0]?.treatment`).
 *
 * @param {object|null|undefined} workflowTask   — the current task object from the API
 * @param {object|null|undefined} leadDetails    — the lead object containing lead_treatments[]
 * @returns {string}  human-readable treatment name, or '' when unresolvable
 */
export function getTaskTreatmentName(workflowTask, leadDetails) {
    if (!workflowTask) return '';

    const lt = workflowTask.lead_treatment;
    if (!lt) return '';

    // Case 1: lead_treatment is a nested object (most common — tasks endpoint nests it).
    if (typeof lt === 'object') {
        return lt.treatment_name || lt.treatment || '';
    }

    // Case 2: lead_treatment is an id — look it up in leadDetails.lead_treatments[].
    const treatments = leadDetails?.lead_treatments;
    if (!Array.isArray(treatments) || treatments.length === 0) return '';
    const matched = treatments.find((t) => String(t?.id) === String(lt));
    return matched ? (matched.treatment_name || matched.treatment || '') : '';
}
